import React, { useEffect, useState } from 'react';
import { Col, Container, Row } from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';
import Authorize from '../../components/AuthComponent/Authorize';
import SaveButton from '../../components/Buttons/SaveButton';
import ButtonBar from '../../components/Fields/ButtonBar';
import DateControl from '../../components/Fields/DateControl';
import Field from '../../components/Fields/Field';
import FieldLabel from '../../components/Fields/FieldLabel';
import Form from '../../components/Fields/Form';
import TextboxField from '../../components/Fields/TextboxField';
import { FormConfig } from '../../components/Fields/Types';
import useValidation from '../../components/Hooks/ValidationHook/useValidation';
import { ApplicationFunctions, useAuth } from '../../contexts/authContext';
import { useNotifications } from '../../contexts/notificationContext';
import ProjectStatusModel from '../../models/ProjectStatusModel';
import WorkflowTaskCategoryModel from '../../models/WorkflowTaskCategoryModel';
import DataService from '../../services/data.service';
import IdParams from '../../types/IdParams';
import { DataResult, Result } from '../../types/Result';
import FormatUtils from '../../utils/FormatUtils';
import Unauthorized from '../Authentication/Unauthorized';

const TaskCategoryView: React.FC = () => {
    const [serverErrors, setServerErrors] = useState<Result>({ isSuccesful: true, messages: [] });
    const [isAuthorized, setIsAuthorized] = useState<boolean>(true);
    const { handleResult, handleException } = useNotifications();
    const navigate = useNavigate();


    const [formData, setFormData] = useState<WorkflowTaskCategoryModel>({
        id: 0,
        name: '',
        beginDate: new FormatUtils().formatDateForStorage(new Date()),
        endDate: '',
        concurrencyId: ''
    });

    let idParams: IdParams = useParams();
    let taskCategoryId: number | undefined;
    if (idParams.id && !Number.isNaN(parseInt(idParams.id))) {
        taskCategoryId = parseInt(idParams.id);
    }

    useEffect(() => {
        if (taskCategoryId !== undefined) {
            DataService.getById<WorkflowTaskCategoryModel>('api/v1/WorkflowTaskCategories/', taskCategoryId)
                .then(response => setFormData(response))
                .catch(e => {
                    if (e.response?.data?.status === 401)
                        setIsAuthorized(false);
                    else
                        handleException(e);
                });
        }
    }, [taskCategoryId]);

    function submit() {
        if (formData) {
            if (taskCategoryId) {
                DataService
                    .update<WorkflowTaskCategoryModel>(`api/v1/WorkflowTaskCategories/`, formData)
                    .then(response => {
                        handleResult(response);
                        setServerErrors(response);
                        if (response.isSuccesful === true && response.data)
                            setFormData(response.data);
                    })
                    .catch(e => handleException(e));
            }
            else {
                DataService
                    .create<number>(`api/v1/WorkflowTaskCategories/`, formData)
                    .then(response => {
                        handleResult(response);
                        setServerErrors(response);
                        if (response.isSuccesful === true && response.data)
                            navigate('/plm/workflowsetup/TaskCategory/' + response.data, { state: { title: 'Task category', fromMenu: false } });
                    })
                    .catch(e => handleException(e));
            }
        }
    }

    const formConfig: FormConfig = {
        fields: [
            { name: 'name', label: 'Name', validationRules: [{ type: 'isRequired' }] },
            { name: 'beginDate', label: 'Active from', validationRules: [{ type: 'isRequired' }, { type: 'isDate' }] },
            { name: 'endDate', label: 'Active Until', labelSize: 'auto', validationRules: [{ type: 'isDate' }] },
        ],
        handleSubmit: submit,
        showErrors: 'always',
    };

    const { getLabelProps, getControlProps, getFieldProps, getSubmitButtonProps } = useValidation(formConfig, formData, serverErrors);

    const { isAuthorizedFor } = useAuth();
    if (taskCategoryId === undefined) {
        if (!isAuthorizedFor(ApplicationFunctions.UpdateWorkflowDefinition)) {
            return (<Unauthorized />);
        }
    }
    else if (!isAuthorized) {
        return (<Unauthorized />);
    }

    return (
        <div className='main-content'>
            <Form title='Task category' footer={(
                <Row>
                    <Col md='12'>
                        <ButtonBar offset={2}>
                            <Authorize applicationFunction={ApplicationFunctions.UpdateWorkflowDefinition}>
                                <SaveButton  {...getSubmitButtonProps()} />
                            </Authorize>
                        </ButtonBar>
                    </Col>
                </Row>
            )}>
                <Container fluid>
                    <Row>
                        <Col md='12'>
                            <TextboxField {...getFieldProps('name')} />
                            <Field {...getLabelProps('beginDate')}>
                                <DateControl {...getControlProps('beginDate')} />
                                <span style={{ padding: '7px 15px' }}><FieldLabel {...getLabelProps('endDate')} /></span>
                                <DateControl {...getControlProps('endDate')} />
                            </Field>
                        </Col>
                    </Row>
                </Container>
            </Form>
        </div>
    );
}
export default TaskCategoryView;